import { useState, useEffect } from "react";
import { gql } from "@apollo/client";

import { apolloClient } from "../../../../graphql/apolloClient";
import { CPProductClass } from "../../../../types/countryProfileEnums";

export interface IGrowthProjectionDatum {
  growth: number;
  abbr: string;
}

export const GROWTH_PROJECTIONS_BULK_QUERY = gql`
  query GrowthProjectionsBulkData($productClass: ProductClass!) {
    countryYear(productClass: $productClass) {
      countryId
      year
      growthProj
    }
    countries: locationCountry {
      countryId
      iso3Code
    }
    dataFlags {
      inRankings
      countryId
    }
  }
`;

interface BulkDataResponse {
  countryYear: Array<{
    countryId: string;
    year: number;
    growthProj: number | null;
  }>;
  countries: Array<{
    countryId: string;
    iso3Code: string;
  }>;
  dataFlags: Array<{
    inRankings: boolean;
    countryId: string;
  }>;
}

function transformGrowthProjections(
  response: BulkDataResponse,
): IGrowthProjectionDatum[] {
  const { countryYear, countries, dataFlags } = response;

  const countryById = new Map(countries.map((c) => [c.countryId, c]));
  const inRankingsSet = new Set(
    dataFlags.filter((f) => f.inRankings).map((f) => f.countryId),
  );

  const latestYear = Math.max(...countryYear.map((d) => d.year));

  const latestYearData = countryYear.filter(
    (d) =>
      d.year === latestYear &&
      inRankingsSet.has(d.countryId) &&
      d.growthProj !== null,
  );

  return latestYearData
    .map((d) => {
      const country = countryById.get(d.countryId);
      if (!country) return null;
      return {
        abbr: country.iso3Code,
        growth: Math.round(d.growthProj! * 100) / 100,
      };
    })
    .filter((d): d is IGrowthProjectionDatum => d !== null);
}

export async function fetchGrowthProjections(): Promise<
  IGrowthProjectionDatum[]
> {
  const result = await apolloClient.query<BulkDataResponse>({
    query: GROWTH_PROJECTIONS_BULK_QUERY,
    variables: { productClass: CPProductClass.HS12 },
    fetchPolicy: "cache-first",
  });
  return transformGrowthProjections(result.data);
}

interface UseGrowthProjectionsDataResult {
  data: IGrowthProjectionDatum[] | undefined;
  loading: boolean;
  error: Error | undefined;
}

export function useGrowthProjectionsData(): UseGrowthProjectionsDataResult {
  const [data, setData] = useState<IGrowthProjectionDatum[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    fetchGrowthProjections()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
